/**
 * summaryGeneration.js — Source assembly for `format=summary` document types.
 *
 * Phase 1 (thin slice): walks the selected summary doc type's prerequisites,
 * pulls the most-recent matching completed documents per `requiredSubcategory`,
 * strips transcripts, projects clinical fields, and concatenates a `combinedText`
 * payload that gets POSTed to `/api/v3/processText` with `sourceType: "summary"`.
 *
 * `prerequisites.sourceCount` is honored if present; otherwise falls back to
 * `prerequisites.minCount` (Phase 1 contract — admin-editable `sourceCount`
 * lands in Phase 2).
 *
 * See: docs/features/document-system/summary-generation.md
 */

/* eslint-env node */
const { selectHistoryDocuments, encounterMillis, formatEncounterDate, clinicalSections, clinicalText, compareEncounters } = require('./historyEvidence');
const getDocDate = doc => encounterMillis(doc) === null ? null : new Date(encounterMillis(doc));

function humanizeSubcategory(value) {
    if (!value) return "required notes";
    return String(value)
        .replace(/[-_]+/g, " ")
        .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function projectClinicalFields(doc) {
  return Object.entries(clinicalSections(doc)).map(([field, value]) =>
    field.replace(/_/g, ' ').toUpperCase() + ': ' + clinicalText(value));
}

/**
 * Build the combinedText payload for a summary generation request.
 *
 * @param {Object} params
 * @param {Object} params.docType            - Selected summary doc type (must have `prerequisites`)
 * @param {Array}  params.patientDocuments   - All patient documents (mixed statuses/types)
 * @param {Array}  params.allDocumentTypes   - All doc types (used to map docType -> subcategory)
 * @param {string} [params.actingUserId]      - Mirrors backend per-document ownership scope
 * @returns {{
 *   combinedText: string,
 *   sourceDocs: Array,
 *   dateRange: ?{start:string,end:string},
 *   unmetPrerequisites: Array,
 *   configurationError: ?string
 * }}
 */
function buildSummarySourceText({
    docType,
    patientDocuments = [],
    allDocumentTypes = [],
    actingUserId = null,
}) {
    const prereqs = Array.isArray(docType?.prerequisites) ? docType.prerequisites : [];
    if (prereqs.length === 0) {
        return {
            combinedText: "",
            sourceDocs: [],
            dateRange: null,
            unmetPrerequisites: [],
            configurationError: "missing_prerequisites",
        };
    }

    // Build maps for resolving a patient doc -> subcategory.
    // Patient docs may carry the new-schema `docType` (Firestore type id),
    // or the legacy numeric `typeId` (matches `documentTypes.legacyId`),
    // or a string typeId equal to the Firestore id. Cover all three.
    // Note: the v2 doc-type transform sets `id` to the legacyId and exposes
    // the real Firestore id as `firestoreId` — handle both shapes.
    const subByTypeId = new Map();   // Firestore documentTypes.id -> subcategory
    const subByLegacy = new Map();   // documentTypes.legacyId (number/string) -> subcategory
    allDocumentTypes.forEach((t) => {
        if (!t?.subcategory) return;
        if (t.firestoreId != null) subByTypeId.set(String(t.firestoreId), t.subcategory);
        if (t.id != null) {
            // v2 shape: id IS the legacyId. v3 shape: id is the Firestore id.
            // Add to both maps to be tolerant.
            subByLegacy.set(String(t.id), t.subcategory);
            subByTypeId.set(String(t.id), t.subcategory);
        }
        if (t.legacyId != null) subByLegacy.set(String(t.legacyId), t.subcategory);
    });

    function resolveSubcategory(d) {
        const candidates = [d.documentTypeId, d.docType, d.docTypeId, d.typeId];
        for (const c of candidates) {
            if (c == null) continue;
            const key = String(c);
            if (subByTypeId.has(key)) return subByTypeId.get(key);
            if (subByLegacy.has(key)) return subByLegacy.get(key);
        }
        return null;
    }

    // Group completed patient docs by subcategory.
    const completed = selectHistoryDocuments(patientDocuments).filter((d) => {
        const author = d?.createdBy || d?.authorId || d?.userId;
        return ["completed", "approved", "exported"].includes(d?.status) &&
            d.isArchived !== true &&
            (!actingUserId || !author || author === actingUserId);
    });
    const bySub = new Map();
    completed.forEach((d) => {
        const sub = resolveSubcategory(d);
        if (!sub) return;
        if (!bySub.has(sub)) bySub.set(sub, []);
        bySub.get(sub).push(d);
    });

    // Match the backend gate before a destination document is created. The
    // backend remains authoritative, but this prevents predictable 422s from
    // leaving empty document shells behind.
    const unmetPrerequisites = prereqs
        .map((p) => {
            const requiredSubcategory = p.requiredSubcategory;
            const minCount = Number(p.minCount) || 0;
            const currentCount = (bySub.get(requiredSubcategory) || []).length;
            return {
                requiredSubcategory,
                requiredSubcategoryName:
                    p.requiredSubcategoryName || humanizeSubcategory(requiredSubcategory),
                minCount,
                currentCount,
            };
        })
        .filter((p) => p.currentCount < p.minCount);

    if (unmetPrerequisites.length > 0) {
        return {
            combinedText: "",
            sourceDocs: [],
            dateRange: null,
            unmetPrerequisites,
            configurationError: null,
        };
    }

    // Per-prereq selection — most recent first, take `sourceCount || minCount`.
    const picked = [];
    prereqs.forEach((p) => {
        const pool = (bySub.get(p.requiredSubcategory) || [])
            .slice()
            .sort(compareEncounters);
        const count = Number(p.sourceCount) > 0 ? Number(p.sourceCount) : Number(p.minCount) || 0;
        picked.push(...pool.slice(0, count));
    });

    // Dedupe (a doc could conceivably appear via multiple prereqs).
    const seen = new Set();
    const uniq = picked.filter((d) => {
        const key = d.id || d.documentId || JSON.stringify(d.createdAt);
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
    });

    // Template source counts define the scope; no additional document-count ceiling.
    uniq.sort(compareEncounters);
    const sourceDocs = uniq.filter(doc => projectClinicalFields(doc).length > 0);

    if (sourceDocs.length === 0) {
        return {
            combinedText: "",
            sourceDocs: [],
            dateRange: null,
            unmetPrerequisites: [],
            configurationError: null,
        };
    }

    // Date range.
    const dates = sourceDocs.map(getDocDate).filter(Boolean).sort((a, b) => a - b);
    const dateRange = dates.length
        ? { start: dates[0].toISOString(), end: dates[dates.length - 1].toISOString() }
        : null;

    // Build combinedText (oldest -> newest reads more naturally for synthesis).
    const ordered = sourceDocs.slice().reverse();
    const combinedText = ordered
        .map((doc, idx) => {
            const lines = [];
            const date = getDocDate(doc);
            const header = date
                ? `=== Document ${idx + 1} (${formatEncounterDate(doc.documentDate)}) ===`
                : `=== Document ${idx + 1} (Date unknown) ===`;
            lines.push(header + ` [source: ${doc.id}; ${doc.historyEvidence?.reviewState || 'unreviewed'}]`);
            lines.push(...projectClinicalFields(doc));
            return lines.join("\n");
        })
        .filter((t) => t && t.length > 20)
        .join("\n\n---\n\n");

    return {
        combinedText,
        sourceDocs,
        dateRange,
        unmetPrerequisites: [],
        configurationError: null,
    };
}

/**
 * Refresh the current patient's documents immediately before summary preflight.
 * Uploads complete asynchronously, so the patient-change snapshot can be stale
 * even though the new source notes are already complete in Firestore.
 */
async function resolveSummaryPatientDocuments({
    patientDocuments = [],
    refreshPatientData,
}) {
    if (typeof refreshPatientData !== "function") {
        return patientDocuments;
    }

    const refreshed = await refreshPatientData();
    if (!Array.isArray(refreshed?.patientDocuments)) {
        throw new Error("Unable to refresh patient documents");
    }

    return refreshed.patientDocuments;
}

function formatSummaryPrerequisiteMessage(summaryName, unmetPrerequisites = []) {
    const label = summaryName || "This summary";
    const details = unmetPrerequisites.map((item) => {
        const name = item.requiredSubcategoryName || humanizeSubcategory(item.requiredSubcategory);
        const noun = item.minCount === 1 ? "note" : "notes";
        return `${item.minCount} ${name} ${noun} required; ${item.currentCount || 0} completed`;
    });
    return `${label} cannot be generated yet: ${details.join("; ")}.`;
}

module.exports = { buildSummarySourceText, resolveSummaryPatientDocuments, formatSummaryPrerequisiteMessage };
